import { Routes, Route, Navigate, Outlet, Link, useLocation, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/providers/AuthProvider";
import { lazy, Suspense, useState, useEffect } from "react";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useSellerLanding } from "@/hooks/useSellerLanding";
import { GuardLoadingScreen } from "@/components/guards/RoleGuard";
import { ZenLoaderScreen } from "@/components/loading/ZenLoader";
import { LoaderVeil } from "@/components/loading/LoaderVeil";
import { useBlankScreenWatchdog } from "@/lib/blank-screen-watchdog";
import { canUseSol } from "@/lib/sol-eligibility";
import { SONG_REQUESTS_ENABLED } from "@/lib/feature-flags";
import {
  OnboardingGuard,
  useOnboardingStatus,
} from "@/components/guards/OnboardingGuard";
import { useAccountStatus } from "@/hooks/useAccountStatus";
import { AccountAccessGuard } from "@/components/guards/AccountAccessGuard";
import { buildOnboardingUrlWithReturn } from "@/lib/auth-redirect";
import { buildRootLoginUrl, buildSignupUrl, normalizeRightsInviteToken } from "@/lib/auth-redirect";

// Lazy load heavy pages
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage }))
);
const SignupPage = lazy(() => import("./auth/signup"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const PendingApprovalPage = lazy(() => import("./pages/PendingApprovalPage"));
const ResetPasswordPage = lazy(() => import("./auth/reset-password"));
const UpdatePasswordPage = lazy(() => import("./auth/update-password"));
const RoleBasedProfile = lazy(() =>
  import("@/components/profile/RoleBasedProfile").then((m) => ({
    default: m.RoleBasedProfile,
  }))
);
const TracksPage = lazy(() => import("./pages/TracksPage"));
const UploadPage = lazy(() =>
  import("./pages/UploadPage").then((m) => ({ default: m.UploadPage }))
);
const DraftsPage = lazy(() => import("./pages/DraftsPage"));
const PlaylistsPage = lazy(() =>
  import("./pages/PlaylistsPage").then((m) => ({ default: m.PlaylistsPage }))
);
const PlaylistDetailPage = lazy(() =>
  import("./pages/PlaylistDetailPage").then((m) => ({
    default: m.PlaylistDetailPage,
  }))
);
const PlaylistAnalyticsPage = lazy(() =>
  import("./pages/PlaylistAnalyticsPage").then((m) => ({
    default: m.PlaylistAnalyticsPage,
  }))
);
const AlbumsPage = lazy(() =>
  import("./pages/AlbumsPage").then((m) => ({ default: m.AlbumsPage }))
);
const ArtistsPage = lazy(() =>
  import("./pages/ArtistsPage").then((m) => ({ default: m.ArtistsPage }))
);
const AlbumDetailPage = lazy(() => import("./pages/AlbumDetailPage"));
const ArtistDetailPage = lazy(() => import("./pages/ArtistDetailPage"));
const PublishersPage = lazy(() => import("./pages/PublishersPage"));
const PublisherDetailPage = lazy(() => import("./pages/PublisherDetailPage"));
const MarketplacePage = lazy(() =>
  import("./pages/MarketplacePage").then((m) => ({
    default: m.MarketplacePage,
  }))
);
const MarketplaceBrowseTracksPage = lazy(() =>
  import("./pages/MarketplaceBrowseTracksPage")
);
const MarketplaceBrowseArtistsPage = lazy(() =>
  import("./pages/MarketplaceBrowseArtistsPage")
);
const MarketplaceBrowseAlbumsPage = lazy(() =>
  import("./pages/MarketplaceBrowseAlbumsPage")
);
const StorefrontDetailPage = lazy(() =>
  import("@/components/marketplace/sections/storefront/StorefrontDetailPage").then((m) => ({
    default: m.StorefrontDetailPage,
  }))
);
const ProjectsPage = lazy(() =>
  import("./pages/ProjectsPage").then((m) => ({
    default: m.ProjectsPage,
  }))
);
const ProjectWorkspacePage = lazy(() =>
  import("./pages/ProjectWorkspacePage").then((m) => ({
    default: m.ProjectWorkspacePage,
  }))
);
const LicenseRequestsPage = lazy(() =>
  import("./pages/LicenseRequestsPage").then((m) => ({
    default: m.LicenseRequestsPage,
  }))
);
const LicenseRequestDetailPage = lazy(() =>
  import("./pages/LicenseRequestDetailPage").then((m) => ({
    default: m.LicenseRequestDetailPage,
  }))
);
const LicenseRequestDealConsolePage = lazy(() =>
  import("./pages/LicenseRequestDealConsolePage").then((m) => ({
    default: m.LicenseRequestDealConsolePage,
  }))
);
const DebugPage = lazy(() =>
  import("./pages/DebugPage").then((m) => ({ default: m.DebugPage }))
);
const RightsHoldersPage = lazy(
  () => import("./rights-holders/RightsHoldersPage")
);
const SearchPage = lazy(() =>
  import("./pages/SearchPage").then((m) => ({ default: m.SearchPage }))
);
const SearchResultsPage = lazy(() =>
  import("./pages/SearchResultsPage").then((m) => ({
    default: m.SearchResultsPage,
  }))
);
const SearchHistoryPage = lazy(() =>
  import("./pages/SearchHistoryPage").then((m) => ({
    default: m.SearchHistoryPage,
  }))
);
const AssistantPage = lazy(() =>
  import("./pages/AssistantPage").then((m) => ({ default: m.AssistantPage }))
);
const ReviewPage = lazy(() => import("@/pages/ReviewPage"));
const SharedPlaylistView = lazy(() =>
  import("@/components/playlists/SharedPlaylistView").then((m) => ({
    default: m.SharedPlaylistView,
  }))
);
const SharedStorefrontPage = lazy(() =>
  import("@/pages/SharedStorefrontPage").then((m) => ({
    default: m.SharedStorefrontPage,
  }))
);
const SharedStorefrontTracksPage = lazy(() =>
  import("@/pages/SharedStorefrontTracksPage")
);
const SharedStorefrontArtistsPage = lazy(() =>
  import("@/pages/SharedStorefrontArtistsPage")
);
const SharedStorefrontAlbumsPage = lazy(() =>
  import("@/pages/SharedStorefrontAlbumsPage")
);
const SharedArtistPage = lazy(() =>
  import("@/pages/SharedArtistPage").then((m) => ({
    default: m.SharedArtistPage,
  }))
);
const TrackPublicPageV5 = lazy(() => import("./pages/TrackPublicPageV5"));
const SharedAlbumPage = lazy(() => import("./pages/SharedAlbumPage"));
const StorefrontArtistDetailPage = lazy(() => import("./pages/StorefrontArtistDetailPage"));
const StorefrontAlbumDetailPage = lazy(() => import("./pages/StorefrontAlbumDetailPage"));
const SettingsPage = lazy(() =>
  import("./components/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);
const AdminLabelsPage = lazy(() =>
  import("./components/admin/AdminLabelsPage").then((m) => ({
    default: m.AdminLabelsPage,
  }))
);
const PartyMergeQueuePage = lazy(() =>
  import("./components/admin/PartyMergeQueuePage").then((m) => ({
    default: m.PartyMergeQueuePage,
  }))
);
const RightsReviewPage = lazy(() =>
  import("./components/admin/RightsReviewPage").then((m) => ({
    default: m.RightsReviewPage,
  }))
);
const AdminDashboardPage = lazy(() =>
  import("./pages/AdminDashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  }))
);
const AdminPublisherMapPage = lazy(() =>
  import("./pages/AdminPublisherMapPage").then((m) => ({
    default: m.AdminPublisherMapPage,
  }))
);
const AdminGuard = lazy(() =>
  import("./components/AdminGuard").then((m) => ({ default: m.AdminGuard }))
);
const HelpCenterPage = lazy(() =>
  import("./pages/HelpCenterPage").then((m) => ({ default: m.HelpCenterPage }))
);
const TermsOfServicePage = lazy(() =>
  import("./pages/TermsOfServicePage").then((m) => ({
    default: m.TermsOfServicePage,
  }))
);
const SellerGuard = lazy(() =>
  import("./components/guards/RoleGuard").then((m) => ({
    default: m.SellerGuard,
  }))
);
const BuyerGuard = lazy(() =>
  import("./components/guards/RoleGuard").then((m) => ({
    default: m.BuyerGuard,
  }))
);
const NonNegotiatorGuard = lazy(() =>
  import("./components/guards/RoleGuard").then((m) => ({
    default: m.NonNegotiatorGuard,
  }))
);
const CollectionsPage = lazy(() =>
  import("./pages/CollectionsPage").then((m) => ({ default: m.CollectionsPage }))
);
const HomePage = lazy(() =>
  import("./pages/HomePage").then((m) => ({ default: m.HomePage }))
);
const RequestSongPage = lazy(() =>
  import("./pages/RequestSongPage").then((m) => ({ default: m.RequestSongPage }))
);
const ConfidentialPage = lazy(() =>
  import("./pages/ConfidentialPage").then((m) => ({ default: m.ConfidentialPage }))
);
const AdminNdasPage = lazy(() =>
  import("./pages/AdminNdasPage").then((m) => ({ default: m.AdminNdasPage }))
);
const AdminCommissionsPage = lazy(() => import("./pages/AdminCommissionsPage"));
const AdminPayoutsPage = lazy(() => import("./pages/AdminPayoutsPage"));
const AdminTaxPage = lazy(() => import("./pages/AdminTaxPage"));
const AdminApprovalsPage = lazy(() =>
  import("./pages/AdminApprovalsPage").then((m) => ({ default: m.AdminApprovalsPage }))
);
const AdminCorporateHierarchyPage = lazy(() =>
  import("./pages/AdminCorporateHierarchyPage").then((m) => ({
    default: m.AdminCorporateHierarchyPage,
  }))
);
/* The customer's own half of the map above: the same table, scoped to the caller's org, showing
   which of their companies a licence request actually reaches. Seller-gated, not admin-gated —
   it is the org curating its own group, not staff curating everyone's. */
const OrgCompanyPage = lazy(() =>
  import("./pages/OrgCompanyPage").then((m) => ({ default: m.OrgCompanyPage }))
);
const AdminCompanyPage = lazy(() =>
  import("./pages/OrgCompanyPage").then((m) => ({ default: m.AdminCompanyPage }))
);
const OrgPublisherMapPage = lazy(() =>
  import("./pages/OrgPublisherMapPage").then((m) => ({ default: m.OrgPublisherMapPage }))
);
const OrgCorporateHierarchyPage = lazy(() =>
  import("./pages/OrgCorporateHierarchyPage").then((m) => ({
    default: m.OrgCorporateHierarchyPage,
  }))
);
const RightsToConfirmPage = lazy(() =>
  import("./pages/RightsToConfirmPage").then((m) => ({ default: m.RightsToConfirmPage }))
);
const RightsHolderInvitePage = lazy(() =>
  import("./pages/RightsHolderInvitePage").then((m) => ({ default: m.RightsHolderInvitePage }))
);

/**
 * Parses the `VITE_SOL_ENABLED` kill switch. Default is ON: an unset or blank
 * var must keep dev and every already-deployed environment working, so only an
 * explicit opt-out takes Sol down.
 *
 * Exported for tests — the flag itself is resolved once at module load because
 * Vite inlines `import.meta.env` at build time.
 */
export function parseSolEnabled(raw: string | undefined): boolean {
  if (typeof raw !== "string") return true;
  const value = raw.trim().toLowerCase();
  if (value === "") return true;
  return !["false", "0", "off", "no"].includes(value);
}

/**
 * Front half of the Sol kill switch; the back half is the backend `SOL_ENABLED`
 * setting, which answers 503 on POST /api/sol/turn. Flipping this off stops the
 * assistant chunk from ever being fetched, so a disabled Sol costs nothing.
 */
const SOL_ENABLED = parseSolEnabled(
  import.meta.env.VITE_SOL_ENABLED as string | undefined
);

/**
 * `/search` IS the assistant now (owner decision, 2026-07-29) — with a floor.
 *
 * Sol cannot serve every account: the backend 403s any non-buyer without a
 * `label_tag`, which is 23 of 58 sellers and all 4 negotiators — 27 users, ~28%
 * of accounts. A bare cutover would take search away from them entirely, so
 * those users keep the classic deterministic SearchPage on the same URL. The
 * same branch is the rollback path: turn VITE_SOL_ENABLED off and EVERY user
 * lands back on the search that has always worked, with no deploy.
 *
 * Anti-flash: gated on `loadedOnce`. An unresolved subject looks label-less, so
 * rendering before it resolves would flash the classic search at a user who is
 * about to get Sol.
 */
function SearchSurface() {
  const caps = useCapabilities();
  if (!caps.loadedOnce) {
    return <GuardLoadingScreen />;
  }
  const solForThisUser =
    SOL_ENABLED &&
    canUseSol({
      accountType: caps.subject.accountType,
      labelTag: caps.subject.labelTag,
    });
  return solForThisUser ? <AssistantPage /> : <SearchPage />;
}

/**
 * The card a route renders in place of a surface that is switched off. A blank
 * page or a silent redirect would read as a broken deploy, so the route stays
 * alive, says what happened, and links to what still works.
 */
function RouteOffNotice({
  title,
  children,
  links,
}: {
  title: string;
  children: React.ReactNode;
  links: { to: string; label: string }[];
}) {
  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card/70 p-8 text-center">
        <h1 className="text-heading-section font-display tracking-tight text-foreground">
          {title}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{children}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-card px-4 text-sm font-medium text-foreground transition-colors hover:border-[var(--zen-purple-border)]"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * What `/assistant` renders while the kill switch is off: hands the user to the
 * search that still works.
 */
function AssistantDisabledNotice() {
  return (
    <RouteOffNotice
      title="AI Search is currently unavailable"
      links={[{ to: "/search", label: "Go to Search" }]}
    >
      The assistant has been turned off temporarily. Everything else works as
      normal — use regular search to keep browsing the catalog.
    </RouteOffNotice>
  );
}

/**
 * What `/request-song` renders while Request a Song is archived
 * (SONG_REQUESTS_ENABLED off). Requests already sent are unaffected and still
 * open from /licenses, so the notice points there.
 */
function SongRequestArchivedNotice() {
  return (
    <RouteOffNotice
      title="Request a Song is paused"
      links={[
        { to: "/home", label: "Go to Home" },
        { to: "/licenses", label: "View license requests" },
      ]}
    >
      You can't send new requests for songs outside the catalog right now.
      Requests you've already sent are still under License Requests.
    </RouteOffNotice>
  );
}

/**
 * Auth gate for surfaces that deliberately render WITHOUT the app shell (the deal
 * console has its own workspace chrome). A viewport-centred loader is correct here
 * precisely because there is no `<main>` for the mark to move into afterwards —
 * the box that centres it is the same one before and after.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <ZenLoaderScreen />;
  if (!user) {
    return (
      <Navigate to={buildRootLoginUrl(location.pathname + location.search)} replace />
    );
  }
  return <>{children}</>;
}

/**
 * The authenticated app shell — and the single gate behind it.
 *
 * THE SHAPE, and why it is this way. The mark used to appear at two different
 * places on every boot: first centred in the VIEWPORT (AuthGuard's loader, which
 * brought its own `min-h-screen`), then centred in `<main>` once Layout mounted —
 * inset by the sidebar (36px collapsed, 120px expanded) and pushed down by the beta
 * banner. Two loaders, two centring boxes, and two veils dissolving back to back at
 * two different points on screen. It read as a glitch because it WAS two different
 * elements, not one moving.
 *
 * Three structural decisions fix it, and each is load-bearing:
 *
 *  1. `Layout` is OUTERMOST. The banner and sidebar paint on the first commit, so
 *     `<main>` exists before any loader does. The centring box is created once and
 *     never changes — the mark is born where it dies. This deletes the second
 *     position rather than compensating for it.
 *
 *  2. ONE gate, not three. `ready` folds auth, onboarding and capabilities into a
 *     single answer, so there is exactly one loader and exactly one dissolve for
 *     the whole boot. `useCapabilities().loadedOnce` already subsumes auth and
 *     label resolution, so this is one flag, not a hand-rolled conjunction.
 *
 *  3. An inner `<Suspense>` around `<Outlet/>`. The outer boundary wraps all of
 *     `<Routes>`, so a lazy page chunk used to suspend this entire subtree —
 *     Layout included — which is WHY the shell could not paint first. Nearest
 *     boundary wins, so this one catches the page chunk and leaves the shell up.
 *
 * Known trade-off: `<Outlet/>` mounts only once the gate opens, so on a cold boot
 * the page chunk downloads after the role lookups rather than alongside them. The
 * seeded subject in `useCapabilities` makes the gate open on the first render for
 * anyone inside the 60s cache window, which is the common case; the cost is one
 * cold boot's worth of serialisation, and the alternative — holding an opaque layer
 * over live content until a flag flips — is the failure mode this whole change set
 * exists to remove.
 */
function ShellGate({ requireOnboarding = true }: { requireOnboarding?: boolean }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const caps = useCapabilities();
  const onboarding = useOnboardingStatus();
  // The account-lifecycle gate, folded into the same single answer as the others. It is NOT part
  // of `useCapabilities`: capabilities say what the account could do, this says whether it may act
  // at all, and a pending buyer holds every buyer capability while being refused everything.
  const account = useAccountStatus();
  const contentRef = useBlankScreenWatchdog<HTMLDivElement>(
    "shell-gate",
    caps.loadedOnce && onboarding !== "loading" && account.access !== "loading"
  );


  // No session → login. Only knowable once auth has actually resolved.
  if (!authLoading && !user) {
    return (
      <Navigate
        to={
          location.pathname.startsWith("/rights/invite/")
            ? buildSignupUrl({
                rightsInviteToken: normalizeRightsInviteToken(
                  location.pathname.slice("/rights/invite/".length)
                ),
              })
            : buildRootLoginUrl(location.pathname + location.search)
        }
        replace
      />
    );
  }

  // Checked BEFORE onboarding, and the order matters: a blocked account is not set up either, and
  // sending them through the wizard would invite them to re-apply into a door that is shut. The
  // onboarding answer says "blocked" for them too (same profile, same rule), so the two can't
  // disagree into a loader that never lifts. `requireOnboarding` does not exempt this — a
  // rights-invite recipient whose account is suspended is still suspended.
  if (account.access === "blocked" || onboarding === "blocked") {
    return <Navigate to="/pending-approval" replace />;
  }

  // /rights/invite/:token opts out: an invited rights holder is legitimately not
  // onboarded yet, and bouncing them into the wizard would lose the invite.
  if (requireOnboarding && onboarding === "redirect") {
    const attempted = `${location.pathname}${location.search}`;
    return <Navigate to={buildOnboardingUrlWithReturn(attempted)} replace />;
  }

  const ready =
    caps.loadedOnce &&
    account.access !== "loading" &&
    (requireOnboarding ? onboarding === "pass" : onboarding !== "loading");

  return (
    <Layout>
      <div ref={contentRef} className="relative flex flex-1 flex-col min-h-0">
        <LoaderVeil ready={ready} loader={<GuardLoadingScreen />}>
          <Suspense fallback={<GuardLoadingScreen />}>
            <Outlet />
          </Suspense>
        </LoaderVeil>
      </div>
    </Layout>
  );
}

/**
 * A public page that gains the app shell (sidebar) for a signed-in user who may use the app.
 *
 * "May use the app" is ShellGate's answer, not just "has a session": an account awaiting approval
 * (or rejected / suspended) or one that has not finished onboarding gets the standalone page, since
 * every sidebar link would only bounce it back out. Signed-out visitors get the standalone page too.
 */
function ConditionalLayoutRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const account = useAccountStatus();
  const onboarding = useOnboardingStatus();

  // useOnboardingStatus never resolves without a session, so it only gates a signed-in user.
  if (loading || (user && (account.access === "loading" || onboarding === "loading"))) {
    return <ZenLoaderScreen />;
  }

  if (user && account.access === "allowed" && onboarding === "pass") {
    return <Layout>{children}</Layout>;
  }

  return <>{children}</>;
}

/**
 * Role-based redirect for authenticated root path.
 * Buyers → /home, negotiators → /licenses, sellers → /tracks, or Your Companies (the company hub) when
 * the seller's organization is set up on the publisher map (Believe first; `useSellerLanding`).
 * Shows loading until role resolves (correctness > speed).
 */
/**
 * What `/` renders. Three outcomes — undecided, signed out, signed in — and the
 * whole point of the shape is that the Zen mark only ever appears in ONE box.
 *
 * This route used to show a viewport-centred `ZenLoaderScreen` while the subject
 * resolved, then redirect into the shell, where the mark reappeared in `<main>` —
 * inset by the sidebar and pushed down by the banner. That was the second half of
 * the jump the shell restructure fixed everywhere else: same mark, two positions,
 * one visible hop. Deciding the redirect from INSIDE the shell puts the mark at the
 * destination's coordinates from the start, so there is nothing to hop.
 *
 * The `authLoading` branch paints the ground and nothing else, deliberately. Any
 * mark drawn before we know whether this is a signed-in user (shell) or a visitor
 * (landing page, no shell) would be drawn in a box we might be about to change —
 * which is the bug. `index.html` already carries the dark background, so this is a
 * continuation of the boot ground, not a flash of something else. It is watchdogged
 * because a `getSession()` that never settles would otherwise sit here silently.
 */
function RoleBasedRootRedirect() {
  const { user, loading: authLoading } = useAuth();
  // Orientation by account type (admin falls through to /tracks, the seller surface). Gated on the
  // anti-flash `loadedOnce` so the redirect target is never computed from a half-resolved subject.
  const { loadedOnce, subject } = useCapabilities();
  // Answered here rather than left to the shell: every target below is inside ShellGate, which
  // would bounce a blocked account straight back out again. One redirect beats two.
  const account = useAccountStatus();
  const groundRef = useBlankScreenWatchdog<HTMLDivElement>("root-entry", !authLoading);
  // Sellers only: a group on the publisher map lands on its company hub. Asked once the subject has
  // resolved and the account may act, so no request goes out for a blocked or half-resolved account.
  const askLanding =
    !!user && loadedOnce && account.access === "allowed" && subject.accountType === "seller";
  const { landing: sellerLanding } = useSellerLanding(askLanding);

  if (authLoading) {
    return <div ref={groundRef} className="min-h-screen bg-background" />;
  }

  if (!user) {
    return <LandingPage />;
  }

  if (account.access === "blocked") {
    return <Navigate to="/pending-approval" replace />;
  }

  if (!loadedOnce || account.access === "loading" || (askLanding && sellerLanding === null)) {
    return (
      <Layout>
        <div className="relative flex flex-1 flex-col min-h-0">
          <GuardLoadingScreen />
        </div>
      </Layout>
    );
  }

  const target =
    subject.accountType === "buyer"
      ? "/home"
      : subject.accountType === "negotiator"
      ? "/licenses"
      : subject.accountType === "seller"
      ? sellerLanding ?? "/tracks"
      : "/tracks";

  return <Navigate to={target} replace />;
}

/**
 * Legacy `/licenses/:id/tracks/:trackId` → the track-scoped Negotiations view of the request
 * detail page. The old route's `:trackId` was the request-track id (the removed per-track page
 * matched it as `detail.tracks.find(t => t.id === selectedTrackId)`, and a deal keys off
 * `request_track_id === track.id`), which is exactly what `?track=` expects — so it maps 1:1.
 * The console scopes to that track and falls back to all tracks on a stale id (never a dead-end).
 */
function LegacyLicenseTrackRedirect() {
  const { id, trackId } = useParams<{ id: string; trackId: string }>();
  const query = trackId
    ? `?view=negotiations&track=${encodeURIComponent(trackId)}`
    : "?view=negotiations";
  return <Navigate to={`/licenses/${id ?? ""}${query}`} replace />;
}

export function AppRoutes() {
  const { user, loading } = useAuth();

  return (
    <Suspense fallback={<ZenLoaderScreen />}>
      <Routes>
        {/* Root redirect */}
        <Route
          path="/"
          // RoleBasedRootRedirect owns all three outcomes (undecided / signed out /
          // signed in) so the mark is never drawn before we know which box it belongs
          // in. Branching out here is what created the viewport-then-shell hop.
          element={<RoleBasedRootRedirect />}
        />

        {/* Public auth routes */}
        <Route path="/auth">
          <Route path="signup" element={<SignupPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="update-password" element={<UpdatePasswordPage />} />
        </Route>

        {/* Onboarding (requires auth, no layout) */}
        <Route path="/onboarding" element={<OnboardingPage />} />

        {/* Other Public Routes */}
        <Route
          path="/help"
          element={
            <ConditionalLayoutRoute>
              <HelpCenterPage />
            </ConditionalLayoutRoute>
          }
        />
        <Route path="/terms" element={<TermsOfServicePage />} />

        {/* Project review links (public, Phase C): the token is the credential */}
        <Route path="/review/:token" element={<ReviewPage />} />

        {/* Shared playlist routes (public) */}
        <Route
          path="/playlist/share/:shareToken"
          element={<SharedPlaylistView />}
        />
        <Route
          path="/playlist/invite/:invitationToken"
          element={<div>Invitation acceptance page - TODO</div>}
        />

        {/* Shared storefront routes (public) */}
        <Route
          path="/storefront/share/:slug/:shareToken"
          element={<SharedStorefrontPage />}
        />
        <Route
          path="/storefront/share/:slug/:shareToken/tracks"
          element={<SharedStorefrontTracksPage />}
        />
        <Route
          path="/storefront/share/:slug/:shareToken/artists"
          element={<SharedStorefrontArtistsPage />}
        />
        <Route
          path="/storefront/share/:slug/:shareToken/albums"
          element={<SharedStorefrontAlbumsPage />}
        />
        <Route
          path="/storefront/share/:slug/:shareToken/artists/:artistId"
          element={<StorefrontArtistDetailPage />}
        />
        <Route
          path="/storefront/share/:slug/:shareToken/albums/:albumId"
          element={<StorefrontAlbumDetailPage />}
        />

        {/* Shared artist routes (public) */}
        <Route path="/shared/artist/:shareId" element={<SharedArtistPage />} />
        {/* Shared album route (public) */}
        <Route path="/shared/album/:shareId" element={<SharedAlbumPage />} />
        {/* The public track page. V5 is the only version — the ?v=2 / ?v=3 switcher and
            the unrouted V4 were retired 2026-08-26 (owner: "i only need it for v5"). Any
            link still carrying ?v= simply gets V5, which is the current page anyway. */}
        <Route path="/track/shared/:trackId" element={<TrackPublicPageV5 />} />

        {/* Playlist detail route - conditionally with Layout for authenticated users */}
        <Route
          path="/playlist/:id"
          element={
            <ConditionalLayoutRoute>
              <PlaylistDetailPage />
            </ConditionalLayoutRoute>
          }
        />

        {/* Protected routes with main layout */}
        <Route element={<ShellGate />}>
          {/* Profile route */}
          <Route path="/profile" element={<RoleBasedProfile />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          {/* The buyer door. Licensees sign up unvetted and wait here until an admin decides;
              rights holders arrive by label invite and never enter this queue. */}
          <Route
            path="/admin/approvals"
            element={
              <AdminGuard>
                <AdminApprovalsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminGuard>
                <AdminDashboardPage />
              </AdminGuard>
            }
          />
          {/* Commission is charged to the payee ORGANIZATION, so this covers publishers and
              distributors too — not just the labels on /admin/labels. */}
          <Route
            path="/admin/commissions"
            element={
              <AdminGuard>
                <AdminCommissionsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/payouts"
            element={
              <AdminGuard>
                <AdminPayoutsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/tax"
            element={
              <AdminGuard>
                <AdminTaxPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/labels"
            element={
              <AdminGuard>
                <AdminLabelsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/ndas"
            element={
              <AdminGuard>
                <AdminNdasPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/parties"
            element={
              <AdminGuard>
                <PartyMergeQueuePage />
              </AdminGuard>
            }
          />
          {/* The merge queue's opposite: /admin/parties says "these two rows are the SAME company",
              this says "these two companies are DIFFERENT and one owns the other". Admin-gated
              because a wrong ownership edge silently rewrites who a track's rights chain points at. */}
          <Route
            path="/admin/corporate-hierarchy"
            element={
              <AdminGuard>
                <AdminCorporateHierarchyPage />
              </AdminGuard>
            }
          />
          {/* Claim requests + holder disputes — the two decisions only staff can make. Approving a
              claim restamps a party's confirmed rights across every seller, so it is admin-gated
              like the merge queue beside it. */}
          <Route
            path="/admin/rights-review"
            element={
              <AdminGuard>
                <RightsReviewPage />
              </AdminGuard>
            }
          />
          {/* Publisher world: every parent publisher and its subpublishers by territory, on a globe.
              Admin only: the data carries contact roles from sync research (require_admin_user on
              GET /api/admin/publisher-map too). */}
          <Route
            path="/admin/publisher-map"
            element={
              <AdminGuard>
                <AdminPublisherMapPage />
              </AdminGuard>
            }
          />
          {/* One publisher-map company for an admin: its overview and its tracks across every label
              (GET /api/admin/publisher-map/companies/{map_id}/...; require_admin_user on the server). */}
          <Route
            path="/admin/publisher-map/companies/:id"
            element={
              <AdminGuard>
                <AdminCompanyPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/publisher-map/companies/:id/music"
            element={
              <AdminGuard>
                <AdminCompanyPage />
              </AdminGuard>
            }
          />

          {/* The org-scoped view of the same ownership map. SellerGuard (CATALOG_WRITE), not
              AdminGuard: every row it can reach is already scoped to the caller's own
              organization server-side, and the customer curating their own group is exactly who
              this is for. */}
          <Route
            path="/corporate-hierarchy"
            element={
              <SellerGuard>
                <OrgCorporateHierarchyPage />
              </SellerGuard>
            }
          />
          {/* One company's workspace (the key is its party id, or a row id when no party is
              named). Same guard and the same page: it reads the key from the path. */}
          <Route
            path="/corporate-hierarchy/:key"
            element={
              <SellerGuard>
                <OrgCorporateHierarchyPage />
              </SellerGuard>
            }
          />

          {/* The organization's own publishing group on the publisher-map globe. SellerGuard, like Your
              Companies: GET /api/org/publisher-map picks the group from the caller's profile on the server
              and leaves out our contact research. */}
          <Route
            path="/publisher-world"
            element={
              <SellerGuard>
                <OrgPublisherMapPage />
              </SellerGuard>
            }
          />

          {/* One company of the organization's map: its overview (catalogue, licensing, syncs, reach) and its
              music, the organization's own tracks the societies register to it. SellerGuard, like Your
              Companies; GET /api/org/company-tracks/{id}... is scoped to the caller's organization on the
              server. */}
          <Route
            path="/companies/:id"
            element={
              <SellerGuard>
                <OrgCompanyPage />
              </SellerGuard>
            }
          />
          <Route
            path="/companies/:id/music"
            element={
              <SellerGuard>
                <OrgCompanyPage />
              </SellerGuard>
            }
          />

          {/* Other protected routes */}
          <Route
            path="/rights-holders"
            element={
              <NonNegotiatorGuard>
                <RightsHoldersPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/tracks"
            element={
              <NonNegotiatorGuard>
                <TracksPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/home"
            element={
              <BuyerGuard>
                <HomePage />
              </BuyerGuard>
            }
          />
          <Route
            path="/collections"
            element={
              <BuyerGuard>
                <CollectionsPage />
              </BuyerGuard>
            }
          />
          {/* Request a Song is archived behind SONG_REQUESTS_ENABLED (default off). The URL
              stays registered so bookmarks and a post-login returnTo land on the notice instead
              of falling through to the `*` redirect; the page chunk is never fetched while off. */}
          <Route
            path="/request-song"
            element={
              <BuyerGuard>
                {SONG_REQUESTS_ENABLED ? <RequestSongPage /> : <SongRequestArchivedNotice />}
              </BuyerGuard>
            }
          />
          <Route
            path="/nda"
            element={
              <BuyerGuard>
                <ConfidentialPage />
              </BuyerGuard>
            }
          />
          {/* The NDA page's old URL. NDA emails and notifications sent before the move link here. */}
          <Route path="/confidential" element={<Navigate to="/nda" replace />} />
          <Route
            path="/upload"
            element={
              <SellerGuard>
                <UploadPage />
              </SellerGuard>
            }
          />
          <Route
            path="/drafts"
            element={
              <SellerGuard>
                <DraftsPage />
              </SellerGuard>
            }
          />
          <Route
            path="/playlists"
            element={
              <NonNegotiatorGuard>
                <PlaylistsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/playlist/:id/analytics"
            element={
              <NonNegotiatorGuard>
                <PlaylistAnalyticsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/albums"
            element={
              <NonNegotiatorGuard>
                <AlbumsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/albums/:id"
            element={
              <NonNegotiatorGuard>
                <AlbumDetailPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/artists"
            element={
              <NonNegotiatorGuard>
                <ArtistsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/artists/:id"
            element={
              <NonNegotiatorGuard>
                <ArtistDetailPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/publishers"
            element={
              <SellerGuard>
                <PublishersPage />
              </SellerGuard>
            }
          />
          <Route
            path="/publishers/:publisherKey"
            element={
              <SellerGuard>
                <PublisherDetailPage />
              </SellerGuard>
            }
          />

          {/* Marketplace route - accessible to both sellers and buyers */}
          <Route
            path="/marketplace"
            element={
              <NonNegotiatorGuard>
                <MarketplacePage />
              </NonNegotiatorGuard>
            }
          />

          {/* Storefront detail page - URL: /marketplace/storefront/:slug/:shareToken */}
          {/* Mode detection happens inside component based on is_owner from API */}
          <Route
            path="/marketplace/storefront/:slug/:shareToken"
            element={
              <NonNegotiatorGuard>
                <StorefrontDetailPage />
              </NonNegotiatorGuard>
            }
          />

          {/* Storefront sub-pages for browsing tracks/artists/albums within a storefront */}
          <Route
            path="/marketplace/storefront/:slug/:shareToken/tracks"
            element={
              <NonNegotiatorGuard>
                <MarketplaceBrowseTracksPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/marketplace/storefront/:slug/:shareToken/artists"
            element={
              <NonNegotiatorGuard>
                <MarketplaceBrowseArtistsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/marketplace/storefront/:slug/:shareToken/albums"
            element={
              <NonNegotiatorGuard>
                <MarketplaceBrowseAlbumsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/marketplace/storefront/:slug/:shareToken/artists/:artistId"
            element={
              <NonNegotiatorGuard>
                <StorefrontArtistDetailPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/marketplace/storefront/:slug/:shareToken/albums/:albumId"
            element={
              <NonNegotiatorGuard>
                <StorefrontAlbumDetailPage />
              </NonNegotiatorGuard>
            }
          />

          <Route
            path="/projects"
            element={
              <BuyerGuard>
                <ProjectsPage />
              </BuyerGuard>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <BuyerGuard>
                <ProjectWorkspacePage />
              </BuyerGuard>
            }
          />
          {/* No role guard: a holder may be a seller or a negotiator (OQ-D is still open), and the
              endpoint returns an empty list to buyers, so the server is the authority on who sees
              what rather than a guard that would have to guess. */}
          <Route path="/rights-to-confirm" element={<RightsToConfirmPage />} />
          <Route path="/licenses" element={<LicenseRequestsPage />} />
          <Route path="/licenses/:id" element={<LicenseRequestDetailPage />} />
          {/* /search is the platform's primary search surface and stays on the
              deterministic SearchPage. Sol is mounted at /assistant only, so the
              agent is an opt-in surface rather than the default path into the
              catalog. */}
          <Route
            path="/search"
            element={
              <NonNegotiatorGuard>
                <SearchSurface />
              </NonNegotiatorGuard>
            }
          />
          {/* Alias of /search, kept only because SearchPage's resume-from-history
              effect still navigates here to clear router state. Delete this route
              together with that navigate() in SearchPage.tsx. */}
          <Route
            path="/search-legacy"
            element={
              <NonNegotiatorGuard>
                <SearchPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/search/:id"
            element={
              <NonNegotiatorGuard>
                <SearchResultsPage />
              </NonNegotiatorGuard>
            }
          />
          <Route
            path="/search/history"
            element={
              <NonNegotiatorGuard>
                <SearchHistoryPage />
              </NonNegotiatorGuard>
            }
          />
          {/* Sol, the agentic assistant. Gated on VITE_SOL_ENABLED so the surface
              can be taken down alongside the backend SOL_ENABLED 503 without a
              code change; while it is off the assistant chunk is never fetched. */}
          <Route
            path="/assistant"
            element={
              <NonNegotiatorGuard>
                {SOL_ENABLED ? <AssistantPage /> : <AssistantDisabledNotice />}
              </NonNegotiatorGuard>
            }
          />

          {/* Debug route */}
          <Route path="/debug" element={<DebugPage />} />
        </Route>

        {/* Where a blocked account is parked. RequireAuth only — deliberately outside ShellGate,
            which is what sends people here: routing it through the shell would loop, and a sidebar
            whose every link bounces back here is worse than one honest page. The page itself
            redirects out once the account is no longer blocked. */}
        <Route
          path="/pending-approval"
          element={
            <RequireAuth>
              <PendingApprovalPage />
            </RequireAuth>
          }
        />

        <Route element={<ShellGate requireOnboarding={false} />}>
          <Route
            path="/rights/invite/:token"
            element={<RightsHolderInvitePage />}
          />
        </Route>

        {/* Deal console uses its own workspace shell, without the global app sidebar. */}
        <Route
          path="/licenses/:id/console"
          element={
            <RequireAuth>
              <AccountAccessGuard>
                <OnboardingGuard>
                  <LicenseRequestDealConsolePage />
                </OnboardingGuard>
              </AccountAccessGuard>
            </RequireAuth>
          }
        />
        {/* Legacy per-track URL — redirects to the per-request console. */}
        <Route
          path="/licenses/:id/tracks/:trackId"
          element={<LegacyLicenseTrackRedirect />}
        />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/tracks" replace />} />
      </Routes>
    </Suspense>
  );
}
